import { FolderOutlined, HomeOutlined, LogoutOutlined, PoweroffOutlined, TransactionOutlined, UserOutlined } from '@ant-design/icons';

import React from 'react';

let homeEntry = {
	title:  'Home',
	single: true,
	top:    true,
	home:   false,
	url:    '/',
	icon:   <HomeOutlined />,
};

const requiredEntries = [
	{
		title:       'My Account',
		permissions: [],
		single:      false,
		top:         true,
		home:        true,
		icon:        <UserOutlined />,
		children:    [
			{
				title:       'User Details',
				url:         '/user-details',
				icon:        <UserOutlined />,
				permissions: [],
			}, {
				title:       'Log Out',
				url:         '/logout',
				icon:        <LogoutOutlined />,
				permissions: [],
			},
		],
	}, {
		title:       'Admin',
		permissions: [ 'admin_show_menu' ],
		single:      false,
		top:         true,
		home:        true,
		icon:        <PoweroffOutlined />,
		children:    [
			{
				title:       'User Management',
				url:         '/admin/user-management',
				permissions: [ 'admin_user_management_read' ],
			},
			{
				title:       'User Groups',
				url:         '/admin/user-groups',
				permissions: [ 'admin_user_groups_read' ],
			},
			{
				title:       'Permissions',
				url:         '/admin/permissions',
				permissions: [ 'admin_permissions_read' ],
			},
			// Database Check is injected here by the nondev/database-check
			// plugin via registerNavChild('Admin', ...).
			// {
			// 	title:       'Services',
			// 	url:         '/admin/services',
			// 	permissions: [ 'admin_manage_services' ],
			// },
			// {
			// 	title:       'Test/Debug Tools',
			// 	url:         '/_tests',
			// 	permissions: [ 'superadmin' ],
			// },
		],
	},
];

export let navigation = [];

// Child entries injected into an existing menu by a plugin, without that menu's
// owner having to reference the plugin. Each: { parentTitle, child, before }.
// Applied in formatNavigation so it works for plugin-registered menus AND the
// hardcoded requiredEntries (e.g. Admin).
//
// Why this is NOT a duplicate of registerNavigation: registerNavigation registers
// or REPLACES a whole top-level menu by title. It cannot add a single child to a
// host-owned menu without redeclaring that menu wholesale, and if two plugins did
// that to the same menu the last one to load would clobber the other's children.
// registerNavChild instead appends children into this shared, deduped, per-parent
// list, so multiple plugins contribute into one menu without colliding. That is
// load-bearing here: database-check, page-access and permissions-by-user all add
// children to Admin, and scheduler and planner add to Work Orders. registerNavigation
// cannot express that. If a purpose-built multi-plugin child-merge mechanism ever
// lands, consolidate onto it; until then this is the piece registerNavigation lacks.
let pendingNavChildren = [];

function registerNavChild(parentTitle, child, opts = {}) {
	// Idempotent like registerNavigation: a plugin module can be evaluated more
	// than once in a session (e.g. dev HMR / lazy re-import), and without this
	// the same child accumulates in pendingNavChildren and gets injected several
	// times - showing up as cloned rows everywhere the nav is consumed (sidebar,
	// Page Access matrix, ...). Match on parent + url (falling back to title) and
	// replace rather than append.
	const entry = { parentTitle, child, before: opts.before || null };
	const idx = pendingNavChildren.findIndex(e =>
		e.parentTitle === parentTitle
		&& (child.url ? e.child.url === child.url : e.child.title === child.title)
	);

	if (idx === -1) {
		pendingNavChildren.push(entry);
	} else {
		pendingNavChildren[idx] = entry;
	}
}

export { registerNavChild };

function applyPendingChildren(nav) {
	pendingNavChildren.forEach(({ parentTitle, child, before }) => {
		let parent = nav.find(row => row.title === parentTitle);

		if(!parent) {
			return;
		}

		parent.children = parent.children || [];

		// Don't re-add a child that's already in this menu (by url, falling back
		// to title), so formatting stays idempotent even if a duplicate slipped
		// into pendingNavChildren or the menu already defines the child itself.
		const already = parent.children.some(c =>
			child.url ? c.url === child.url : c.title === child.title
		);

		if(already) {
			return;
		}

		let insertAt = before
			? parent.children.findIndex(c => c.title === before)
			: -1;

		if(insertAt === -1) {
			parent.children.push(child);
		} else {
			parent.children.splice(insertAt, 0, child);
		}
	});

	return nav;
}





function formatNavigation(config) {
	// Clone each row with a fresh children array so injecting pending children is
	// idempotent across the many times this runs (called on every render).
	// Menu order: Home first, then plugin-registered menus alphabetically by
	// title (registration order follows plugin load order, which is not
	// meaningful), then My Account and Admin last. Children keep their curated
	// order (plugins place them deliberately via registerNavChild's `before`).
	let nav = [
		homeEntry,
		...[ ...config ].sort((a, b) => a.title.localeCompare(b.title)),
		...requiredEntries,
	].map(row => ({
		...row,
		children: [ ...(row.children || []) ],
	}));

	applyPendingChildren(nav);

	// Admin's children come from both the host (requiredEntries) and several
	// plugins (database-check, page-access, permissions-by-user, reports), so
	// their combined order follows registration order. Keep them alphabetical
	// instead. Other menus keep their curated child order.
	const adminEntry = nav.find(row => row.title === 'Admin');
	if (adminEntry && adminEntry.children) {
		adminEntry.children.sort((a, b) => a.title.localeCompare(b.title));
	}

	return nav.map(row => ({
		title:       'Default Title',
		single:      false,
		icon:        null,
		top:         true,
		home:        true,
		...row,
		permissions: Array.isArray(row.permissions)
			? row.permissions
			: [],
		children: (row.children || []).map(child => ({
			...child,
			permissions: Array.isArray(child.permissions)
				? child.permissions
				: [],
		})),
		routes: (row.children || []).map(child => child.url),
	}));
}


function registerNavigation(navEntry) {
	let navIndex = navigation.findIndex(row => row.title === navEntry.title);

	if(navIndex === -1) {
		navigation.push(navEntry);
	} else {
		navigation[navIndex] = navEntry;
	}
}

export { registerNavigation };

let formattedNavigation = () => (formatNavigation(navigation));

export default formattedNavigation;

